// ============================================================
// ✅ EditServiceModal.jsx - Modification d'un service
// Support multi-tenant : Global + Spécifique
// ============================================================

import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import { X, AlertCircle, Globe } from 'lucide-react';

export default function EditServiceModal({ isOpen, onClose, onSuccess, service }) {
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [departements, setDepartements] = useState([]);
  const [societes, setSocietes] = useState([]);
  
  const [formData, setFormData] = useState({
    Nom_Service: '', ID_Departement: '', ID_Societe: '', EstGlobal: false
  });

  const { user } = useUser();
  const isSuperAdmin = user?.EstSuperAdmin === 1 || user?.EstSuperAdmin === true;

  useEffect(() => {
    if (isOpen && service) {
      const estGlobal = service.ID_Societe === null || service.ID_Societe === undefined;
      setFormData({
        Nom_Service: service.Nom_Services || service.Nom_Service || '',
        ID_Departement: service.ID_Departement || '',
        ID_Societe: service.ID_Societe || '',
        EstGlobal: estGlobal
      });
      fetchDepartements();
      if (isSuperAdmin) fetchSocietes();
    }
  }, [isOpen, service]);

  const fetchDepartements = async () => {
    try {
      const res = await api.get('/departements', { params: { ID_Societe: isSuperAdmin ? null : user?.ID_Societe } });
      setDepartements(res.data.departements || []);
    } catch (err) { console.error('Erreur chargement départements:', err); }
  };

  const fetchSocietes = async () => {
    try { const res = await api.get('/societes'); setSocietes(res.data.societes || []); }
    catch (err) { console.error('Erreur chargement sociétés:', err); }
  };

  const handleGlobalToggle = (checked) => {
    setFormData({ ...formData, EstGlobal: checked, ID_Societe: checked ? '' : (service.ID_Societe || user?.ID_Societe || '') });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.Nom_Service.trim()) { setError('Le nom du service est requis'); return; }
    if (!formData.ID_Departement) { setError('Veuillez sélectionner un département'); return; }
    setLoading(true); setError('');
    try {
      await api.put(`/services/${service.ID_Services || service.ID_Service}`, {
        Nom_Service: formData.Nom_Service,
        ID_Departement: formData.ID_Departement,
        ID_Societe: formData.EstGlobal ? null : formData.ID_Societe,
        EstSuperAdmin: isSuperAdmin ? 1 : 0
      });
      onSuccess(); onClose();
    } catch (err) { setError(err.response?.data?.message || 'Erreur lors de la modification'); }
    finally { setLoading(false); }
  };

  if (!isOpen || !service) return null;

  const estGlobal = service.ID_Societe === null || service.ID_Societe === undefined;
  const peutModifier = isSuperAdmin || !estGlobal;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-blue-600">✏️ Modifier le service</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        {!peutModifier && <div className="mb-4 p-3 bg-amber-50 text-amber-700 text-sm rounded-lg flex items-center gap-2"><AlertCircle size={16} /> Seul le SuperAdmin peut modifier un service global.</div>}
        {error && <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg flex items-center gap-2"><AlertCircle size={16} /> {error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom du service *</label>
            <input type="text" className="w-full p-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              value={formData.Nom_Service} onChange={(e) => setFormData({ ...formData, Nom_Service: e.target.value })} required disabled={!peutModifier} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Département *</label>
            <select className="w-full p-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              value={formData.ID_Departement} onChange={(e) => setFormData({ ...formData, ID_Departement: e.target.value })} required disabled={!peutModifier}>
              <option value="">Sélectionner un département</option>
              {departements.map(d => <option key={d.ID_Departement} value={d.ID_Departement}>{d.Nom_Departement}</option>)}
            </select>
          </div>
          {isSuperAdmin && (
            <label className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg cursor-pointer border border-purple-200">
              <input type="checkbox" checked={formData.EstGlobal} onChange={(e) => handleGlobalToggle(e.target.checked)} className="w-4 h-4 text-purple-600 rounded" />
              <div><span className="text-sm font-medium text-purple-700 flex items-center gap-1"><Globe size={14} /> Service Global</span><p className="text-xs text-purple-500">Visible par toutes les sociétés</p></div>
            </label>
          )}
          {!formData.EstGlobal && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Société *</label>
              <select className="w-full p-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                value={formData.ID_Societe || ''} onChange={(e) => setFormData({ ...formData, ID_Societe: e.target.value })} required disabled={!peutModifier}>
                <option value="">Sélectionner une société</option>
                {societes.map(s => <option key={s.ID_Societe} value={s.ID_Societe}>{s.RaisonSociale} {s.Sigle ? `(${s.Sigle})` : ''}</option>)}
              </select>
            </div>
          )}
          {peutModifier && (
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition">Annuler</button>
              <button type="submit" disabled={loading} className="flex-1 px-4 py-2.5 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 transition disabled:opacity-50">{loading ? 'Enregistrement...' : 'Modifier'}</button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}